import { useState, FormEvent, useRef, useEffect, Fragment } from 'react';
import "./style.css"

import { Form, Button, Image } from 'react-bootstrap';
import { FiSend, FiRotateCcw, FiMessageCircle, FiUser } from 'react-icons/fi';
// import { IChatHistory } from '../models';
import { useMutation, useQuery } from 'react-query';
import { getChatAgent, getChatResponse } from '../api/agent';
// import ReactLoading from 'react-loading';
import { useParams } from 'react-router-dom';
import { nanoid } from 'nanoid';
import WordByWordDisplay from '../components/Configure/WordByWordDisplay';
import { ISimpleMessage } from '../models';

function EmbedChatbotPage() {
    const [sessionId, setSessionId] = useState<string>(localStorage.getItem('quickyChatSessionId') || nanoid())
    const [question, setQuestion] = useState<string>('');
    const [newMessage, setNewMessage] = useState(false);
    const [chatHistory, setChatHistory] = useState<ISimpleMessage[]>(() => {
        const storedHistory = localStorage.getItem('quickyChatHistory');
        return storedHistory ? JSON.parse(storedHistory) : [];
    });
    const baseURL = process.env.REACT_APP_API_URL!;
    const chatBoxRef = useRef<HTMLDivElement | null>(null);
    const accessKey = useParams().accessKey;
    
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    // chat mutation
    // const queryClient = useQueryClient();
    const {mutateAsync: getChatResponseFn, isLoading,} = useMutation(getChatResponse, {
        onSuccess: (data) => {
            // setChatHistory((prevChatHistory) => [...prevChatHistory, data]);
            setNewMessage(true);
            updateChatHistory(data.message);
        },
        onError: (error) => {
            const content = "Sorry, something went wrong. Please try again, reload browser, or contact support.";
            setChatHistory([...chatHistory, {role: "error", content}]);
        }
    });

    const {data: agent} = useQuery({
        queryKey: ['Agents', accessKey],
        queryFn: () => getChatAgent(accessKey!),
        enabled: !!accessKey
    });


    const updateChatHistory = (newMessage: ISimpleMessage) => {
        setChatHistory((prevHistory) => {
            const updatedHistory = [...prevHistory, newMessage].slice(-20); // keep lastest 20 messages
            localStorage.setItem(`quickyChatHistory`, JSON.stringify(updatedHistory));
            return updatedHistory;
        });
    };      

    const resetChatbot = () => {
        //remove previous sessionId and chatHistory from localstorage
        localStorage.removeItem('quickyChatSessionId');
        localStorage.removeItem('quickyChatHistory');
        // new sessionId and clear chatHistory
        setSessionId(nanoid());
        setChatHistory([{"role": "system", content: agent?.greeting || "Hi there, how can I help you today?" }]);
    }

    const adjustHeight = () => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (question.trim() && !isLoading) {
                handleSendMessage(e as any);
            }
        } else if (e.key === 'Enter' && e.shiftKey) {
            // Allow default behavior for new line
            setTimeout(adjustHeight, 0);
        }
    };

    const handleSendMessage = (e: FormEvent) => {
        e.preventDefault();
        if (!question.trim() || !accessKey) return;

        // update chat History
        const message = {role:'user', content: question };
        // const newChatHistory = [...chatHistory, message];
        // setChatHistory(newChatHistory);
        updateChatHistory(message);
        setQuestion(''); // Clear the input field
    
        // get http request
        // console.log(newChatHistory);
        getChatResponseFn({
            accessKey: accessKey,
            sessionId,
            message
        })
    };

    useEffect(() => {
        // Only store the session ID if it wasn't already in localStorage
        if (!localStorage.getItem('quickyChatSessionId')) {
        localStorage.setItem('quickyChatSessionId', sessionId);
        }
    }, [sessionId]);

    useEffect(() => {
        if (chatBoxRef.current) {
        chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight; // Scroll to bottom
        }
    }, [chatHistory, /*isLoading*/]); // Depend on chatHistory and isLoading

    useEffect(()=>{
        if (agent && chatHistory.length === 0) 
            updateChatHistory({"role": "system", content: agent.greeting || "Hi there, how can I help you today?" });
    }, [agent, chatHistory])

    const avatarImg = agent?.avatarImageURL ? baseURL + agent.avatarImageURL : '/images/chat-icon.svg';
    const launcherImg = agent?.launcherImageURL ? baseURL + agent.launcherImageURL : '/images/chat-icon.svg';

    return (
        <div className="embed-chat-container">
            {!agent ? (
                <div className="error-state">
                    <FiMessageCircle size={48} className="error-icon" />
                    <h3>Chat Unavailable</h3>
                    <p>Unable to load the chatbot. Please try again later.</p>
                </div>
            ) : (
                <div className="chat-interface">
                    {/* Modern Chat Header */}
                    <div className="chat-header" style={{ backgroundColor: agent.primaryColor }}>
                        <div className="header-content">
                            <div className="bot-info">
                                <div className="avatar-container">
                                    <Image
                                        src={avatarImg}
                                        className="bot-avatar"
                                        alt={agent.displayName}
                                    />
                                    <div className="status-indicator"></div>
                                </div>
                                <div className="bot-details">
                                    <h4 className="bot-name">{agent.displayName}</h4>
                                    <span className="bot-status">Online</span>
                                </div>
                            </div>
                            <Button 
                                onClick={resetChatbot}
                                className="reset-button"
                                variant="link"
                                title="Start new conversation"
                            >
                                <FiRotateCcw size={18} />
                            </Button>
                        </div>
                    </div>

                    {/* Enhanced Chat Messages */}
                    <div className="chat-messages" ref={chatBoxRef}>
                        {chatHistory.length > 0 ? (
                            chatHistory.map((msg, index) => (
                                <div key={index} className={`message-container ${msg.role}`}>
                                    {msg.role === 'user' ? (
                                        <div className="message-wrapper user-message">
                                            <div className="message-bubble user-bubble" 
                                                 style={{ backgroundColor: agent.primaryColor }}>
                                                {msg.content.split("\n").map((line, indx) => (
                                                    <Fragment key={indx}>
                                                        {line}
                                                        {indx < msg.content.split("\n").length - 1 && <br />}
                                                    </Fragment>
                                                ))}
                                            </div>
                                            <div className="message-avatar">
                                                <FiUser size={16} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="message-wrapper bot-message">
                                            <div className="message-avatar">
                                                <Image
                                                    src={avatarImg}
                                                    className="avatar-small"
                                                    alt={agent.displayName}
                                                />
                                            </div>
                                            <div className={`message-bubble bot-bubble ${msg.role === 'error' ? 'error-bubble' : ''}`}>
                                                {(index + 1 === chatHistory.length && newMessage) ? (
                                                    <WordByWordDisplay text={msg.content} />
                                                ) : (
                                                    msg.content.split("\n").map((line, indx) => (
                                                        <Fragment key={indx}>
                                                            {line}
                                                            {indx < msg.content.split("\n").length - 1 && <br />}
                                                        </Fragment>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <FiMessageCircle size={32} className="empty-icon" />
                                <p>Start a conversation by typing a message below</p>
                            </div>
                        )}

                        {isLoading && (
                            <div className="message-container assistant">
                                <div className="message-wrapper bot-message">
                                    <div className="message-avatar">
                                        <Image
                                            src={launcherImg}
                                            className="avatar-small"
                                            alt={agent.displayName}
                                        />
                                    </div>
                                    <div className="message-bubble bot-bubble typing-indicator">
                                        <div className="typing-dots">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Modern Input Form */}
                    <div className="chat-input-container">
                        <Form onSubmit={handleSendMessage} className="chat-form">
                            <div className="input-wrapper">
                                <Form.Control
                                    ref={textareaRef}
                                    as="textarea"
                                    rows={1}
                                    placeholder="Type your message..."
                                    value={question}
                                    onInput={adjustHeight}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="message-input"
                                    disabled={isLoading}
                                />
                                <Button 
                                    type="submit"
                                    className="send-button"
                                    disabled={!question.trim() || isLoading}
                                    style={{ 
                                        backgroundColor: agent.primaryColor,
                                        borderColor: agent.primaryColor 
                                    }}
                                >
                                    <FiSend size={18} />
                                </Button>
                            </div>
                        </Form>
                        
                        {!agent.hidePoweredBy && (
                            <div className="powered-by">
                                <span>Powered by </span>
                                <a href="https://quickychat.com/" target="_blank" rel="noopener noreferrer">
                                    QuickyChat.com
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmbedChatbotPage;
